import { Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Change,
  changeOf,
  niceRound,
  paddedDomain,
  quantile,
  symlog,
} from "../../shared/chartMath";
import {
  signedTone,
  TABULAR_NUMERALS,
  Tone,
  toneColors,
} from "../general/dataAtoms/shared.ts";
import { NetResultRangeStrip } from "./NetResultRangeStrip.tsx";
import { ANNOTATION_FONT_SIZE_PX } from "./scatter/scatterDataset.ts";
import {
  REFERENCE_LINE_WIDTH,
  ScatterAxis,
  ScatterOverlay,
  ScatterZone,
  toneZoneColors,
  ZonedScatter,
} from "./scatter/ZonedScatter.tsx";

interface NetRange {
  worst: number;
  avg: number;
  best: number;
}

export interface NetResultPoint {
  caseId: string;
  caseName: string;
  // net-result difference from settling = after.avg − before.avg (the x metric)
  diff: number;
  // whole-case net-result range before settling (litigated) and after (settled)
  before: NetRange;
  after: NetRange;
  claimValue: number;
  // claim value normalised 0…1 over all settled cases (drives y; bigger = higher)
  claimNorm: number;
}

/**
 * Which change settling made is being plotted. Both are the same shape — a
 * signed per-case difference against the litigated world — so they share every
 * mechanic and differ only in the value read off each case and the wording
 * around it.
 */
export type SettledVsLitigatedMetric = "netResult" | "exposure";

const valueOf = (p: NetResultPoint, metric: SettledVsLitigatedMetric) =>
  metric === "netResult" ? p.diff : p.after.worst - p.before.worst;

// the zones in the order of the divided bar: settled below the litigated
// figure | at it | above it, read the way the tooltip words the same difference
const ZONES = [
  { change: "worse", tone: "adverse", label: "reduced" },
  { change: "unchanged", tone: "neutral", label: "unchanged" },
  { change: "better", tone: "favourable", label: "improved" },
] as const satisfies readonly { change: Change; tone: Tone; label: string }[];

/**
 * Settled-vs-litigated scatter (mirrors the bargaining-analysis scatter): one
 * dot per settled case, x = how much settling changed the chosen metric on a
 * symmetric-log scale centred at 0, y = claim size. Right of the solid "no
 * change" line settling helped (the favourable tone), left it did not (the
 * adverse tone), and a case on the line is one settling left as it was (grey).
 * The symmetric-log axis keeps the 0 anchor well spread while compressing
 * extreme differences into the tails instead of clipping.
 */
export const SettlementNetResultChart = ({
  title,
  subtitle,
  points,
  metric = "netResult",
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  points: NetResultPoint[];
  metric?: SettledVsLitigatedMetric;
  // shown in place of the chart when there is nothing to plot
  emptyMessage: string;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.settledVsLitigated",
  });

  const view = useMemo(() => {
    const diffs = points.map((p) => valueOf(p, metric));
    const absSorted = diffs.map(Math.abs).sort((a, b) => a - b);
    // linear-zone half-width: ~75th percentile of |diff|, so most cases sit in
    // the linear middle and only outliers get log-compressed
    const L = Math.max(1, niceRound(quantile(absSorted, 0.75)));
    // symmetric-log transform centred on 0 (settling changed nothing)
    const { forward, inverse } = symlog(L);
    const sorted = [...diffs].sort((a, b) => a - b);
    const median = quantile(sorted, 0.5);
    const avg = diffs.reduce((s, d) => s + d, 0) / (diffs.length || 1);
    // the axis always holds 0, so the "no change" line is on screen
    const { min: axisMin, max: axisMax } = paddedDomain([
      0,
      ...diffs.map(forward),
    ]);
    const ticks = [-100, -50, -20, -10, -5, -2, -1, 0, 1, 2, 5, 10, 20, 50, 100]
      .map((m) => forward(m * L))
      .filter((v) => v >= axisMin && v <= axisMax);
    return { forward, inverse, median, avg, axisMin, axisMax, ticks };
  }, [points, metric]);

  const zones = useMemo<ScatterZone<NetResultPoint>[]>(() => {
    const colors = toneZoneColors(theme);
    return ZONES.map(({ change, tone, label }) => {
      const inZone = points.filter(
        (p) => changeOf(valueOf(p, metric)) === change,
      );
      return {
        key: change,
        points: inZone,
        color: colors[tone],
        label: t(`${metric}.${label}`, { count: inZone.length }),
      };
    });
  }, [points, metric, theme, t]);

  const toXY = useCallback(
    (p: NetResultPoint) => ({
      x: view.forward(valueOf(p, metric)),
      y: p.claimNorm,
    }),
    [view, metric],
  );

  const xAxis = useMemo<ScatterAxis>(
    () => ({
      min: view.axisMin,
      max: view.axisMax,
      ticks: view.ticks,
      formatTick: (value) => currency.formatCompact(view.inverse(value)),
    }),
    [view, currency],
  );

  // solid reference line at 0 (settling made no difference)
  const drawZeroLine: ScatterOverlay = ({ ctx, chartArea, scales }) => {
    if (!scales.x) return;
    const x = scales.x.getPixelForValue(0);
    ctx.save();
    ctx.strokeStyle = theme.palette.text.secondary;
    ctx.lineWidth = REFERENCE_LINE_WIDTH;
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.font = `600 ${ANNOTATION_FONT_SIZE_PX}px ${theme.typography.fontFamily}`;
    ctx.fillStyle = theme.palette.text.secondary;
    ctx.textAlign = "center";
    ctx.fillText(t("noChange"), x, chartArea.top - 6);
    ctx.restore();
  };

  const describeDelta = (p: NetResultPoint) => {
    const d = valueOf(p, metric);
    switch (changeOf(d)) {
      case "unchanged":
        return t(`${metric}.deltaSame`);
      case "better":
        return t(`${metric}.deltaBetter`, { amount: currency.format(d) });
      case "worse":
        return t(`${metric}.deltaWorse`, { amount: currency.format(-d) });
    }
  };

  const tones = toneColors(theme);
  // a change of nothing is neither good nor bad, and stays in ink
  const statColor = (change: number) => {
    const tone = signedTone(change);
    return tone === "neutral" ? undefined : tones[tone];
  };

  return (
    <ZonedScatter
      title={title}
      subtitle={subtitle}
      ariaLabel={t(`${metric}.chartLabel`)}
      stats={[
        {
          label: t("medianChange"),
          value: currency.format(view.median),
          color: statColor(view.median),
        },
        {
          label: t("avgChange"),
          value: currency.format(view.avg),
          color: statColor(view.avg),
        },
      ]}
      zones={zones}
      toXY={toXY}
      xAxis={xAxis}
      drawOverlay={drawZeroLine}
      overlayHasTopLabels
      emptyMessage={emptyMessage}
      renderTooltip={(point) => (
        <>
          <Typography variant="body2" sx={{ mt: 0.5, ...TABULAR_NUMERALS }}>
            {describeDelta(point)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
          >
            {t("tooltipClaim", { claim: currency.format(point.claimValue) })}
          </Typography>
          <NetResultRangeStrip
            before={point.before}
            after={point.after}
            metric={metric}
          />
        </>
      )}
    />
  );
};
